/**
 * Hosts the Quick item dialog and the whole item page over a screen: the opener its item buttons use
 * (ItemEditorOpenerContext), and the dialogs themselves, loaded on first use. Every page has one
 * (SiteItemEditorHost, in MainLayout); a screen with its own (the Menu Cycle editor, Menu Import, the
 * Basic menu) nests it.
 *
 * Nothing here opens a new browser tab (Jeff 2026-11-03: "stay in same tab. no switching tabs."). "Open
 * full item" opens the whole item page over the screen (ItemEditorDialog, as Validation does; Lynbrook
 * 10-28: the new tab was confusing), and on a new item that is not saved yet it saves it first. Closing
 * it comes back to the screen; a save there tells the screen as any item save does (announceItemSaved).
 *
 * Levels match the item page (frmDietech_Item_AddMod.Set_Security): the dialog edits an item for Items
 * level 2 and up and adds one for level 3 and up. Level 1 views: Open item opens the whole item page,
 * read only. Below level 1 there is no opener.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { usePermission } from '../../hooks/usePermission';
import type { ItemEditorDto } from '../../api/itemEditorApi';
import { ITEMS_LEVEL } from './itemRules';
import type { ItemEditorOpener, PickedItem } from './itemEditorOpener';

const loadQuickItemDialog = () => import('./QuickItemDialog');
const QuickItemDialog = lazy(loadQuickItemDialog);
const ItemEditorDialog = lazy(() => import('./ItemEditorDialog'));

interface Session {
  /** A new dialog per opening, so its draft starts fresh. */
  key: number;
  itemId: number;
  section?: string;
  /** A new item for a picker: gets the item once it's saved. */
  onPicked?: (item: PickedItem) => void;
  /** New From Existing: the item the new one starts as a copy of. */
  fromItemId?: number;
}

/** The whole item page open over the screen. */
interface FullSession {
  key: number;
  itemId: number;
  section?: string;
}

export interface QuickItemHost {
  /** Null for users who can't see items (Items level 0), and while the screen turns it off. */
  opener: ItemEditorOpener | null;
  /** The dialog while it's open; render it once. */
  element: ReactNode;
}

export interface QuickItemHostOptions {
  /** False while the screen can't use the dialog (a phone, or a level that can't add to the screen): no opener, nothing loaded. Default true. */
  enabled?: boolean;
  /**
   * Fetch the dialog's code once the screen is up, so the first open isn't a moment of nothing. Default true;
   * the host round every page (SiteItemEditorHost) loads it on first use instead.
   */
  preload?: boolean;
}

/** The picked item a saved new item sends back: its key and name, and its size when it has exactly one. */
export function pickedFromSaved(saved: ItemEditorDto): PickedItem {
  const sizes = saved.sizes.filter((s) => s.itm_Size != null);
  return { itemId: saved.item.pKey_Item, itemName: saved.item.itm_Descr ?? '', sizeId: sizes.length === 1 ? sizes[0].pKey_Itm_Size : null };
}

export function useQuickItemHost(facilityId: number, options: QuickItemHostOptions = {}): QuickItemHost {
  const { enabled = true, preload = true } = options;
  const { itemsLevel } = usePermission();
  const canView = enabled && itemsLevel >= ITEMS_LEVEL.VIEW;
  const canEdit = enabled && itemsLevel >= ITEMS_LEVEL.MODIFY;
  const [session, setSession] = useState<Session | null>(null);
  const [full, setFull] = useState<FullSession | null>(null);
  useEffect(() => {
    if (canEdit && preload) void loadQuickItemDialog().catch(() => undefined);
  }, [canEdit, preload]);

  const openFull = useCallback((itemId: number, section?: string) => {
    if (!(itemId > 0)) return;
    /* The quick dialog gives way to the whole page (it has already asked about unsaved changes). */
    setSession(null);
    setFull({ key: Date.now(), itemId, section });
  }, []);
  /* Edit from level 2; a role that can only view gets the whole item page, read only. */
  const openItem = useCallback((itemId: number, section?: string) => {
    if (!(itemId > 0)) return;
    if (canEdit) setSession({ key: Date.now(), itemId, section });
    else openFull(itemId, section);
  }, [canEdit, openFull]);
  /* Always the dialog: a role below level 3 sees there that it can't add items. */
  const newItem = useCallback((onPicked: (item: PickedItem) => void, fromItemId?: number) => {
    setSession({ key: Date.now(), itemId: 0, onPicked, fromItemId: fromItemId != null && fromItemId > 0 ? fromItemId : undefined });
  }, []);
  const opener = useMemo<ItemEditorOpener | null>(() => (canView ? { openItem, newItem, openFull } : null), [canView, openItem, newItem, openFull]);

  const close = useCallback(() => setSession(null), []);
  const closeFull = useCallback(() => setFull(null), []);
  const quick = session && facilityId > 0 ? (
    <Suspense fallback={null}>
      <QuickItemDialog
        key={session.key}
        facilityId={facilityId}
        itemId={session.itemId}
        fromItemId={session.fromItemId}
        section={session.section}
        onClose={close}
        onSaved={(saved, wasNew) => {
          if (wasNew && session.onPicked && saved?.item?.pKey_Item > 0) session.onPicked(pickedFromSaved(saved));
        }}
        onOpenFull={openFull}
      />
    </Suspense>
  ) : null;
  const fullPage = full && facilityId > 0 ? (
    <Suspense fallback={null}>
      <ItemEditorDialog key={full.key} open itemId={full.itemId} section={full.section} onClose={closeFull} />
    </Suspense>
  ) : null;

  return { opener, element: <>{quick}{fullPage}</> };
}
